/**
 * Loads the Google Maps JavaScript API once and resolves with window.google.maps
 * only when the core library (Map, Marker, Circle) is actually usable. Safe to
 * call from multiple components — the script is injected a single time and
 * every caller shares the same promise.
 *
 * Two things went wrong before this version (dev, 2026-09-23): public/index.html
 * already loads the API, and when a map component mounted before that tag had
 * finished, this loader injected a SECOND copy (with loading=async). Two copies
 * of the API on one page break lazy module loading ("Loader.provide not called
 * by module 'poly'") and the Circle/Map constructors throw, so the suburb map
 * fell back to "Map unavailable". And `window.google.maps` exists before the
 * core library is ready under loading=async, so "available" was not "usable".
 *
 * Returns a rejected promise when no API key is configured or the script fails to
 * load, so callers can fall back to a non-map flow (manual coordinates, GPS).
 */
let loadPromise = null;

const SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const READY_POLL_MS = 50;
const READY_TIMEOUT_MS = 15000;

/** The API object exists. Not the same as usable — see mapsReady(). */
export const isGoogleMapsAvailable = () =>
  typeof window !== 'undefined' && !!window.google && !!window.google.maps;

/** The core library is loaded: constructors are real functions, not lazy stubs. */
const mapsReady = () => {
  const m = typeof window !== 'undefined' && window.google && window.google.maps;
  return !!m && typeof m.Map === 'function' && typeof m.Marker === 'function' && typeof m.Circle === 'function';
};

/** Resolve once the core library is usable; under loading=async ask for it explicitly. */
const waitForReady = () => new Promise((resolve, reject) => {
  if (mapsReady()) return resolve(window.google.maps);
  const m = window.google && window.google.maps;
  if (m && typeof m.importLibrary === 'function') {
    Promise.all([m.importLibrary('maps'), m.importLibrary('marker')])
      .then(() => (mapsReady() ? resolve(window.google.maps) : reject(new Error('Google Maps core library did not load'))))
      .catch(reject);
    return;
  }
  const started = Date.now();
  const tick = () => {
    if (mapsReady()) return resolve(window.google.maps);
    if (Date.now() - started > READY_TIMEOUT_MS) return reject(new Error('Google Maps did not become ready'));
    setTimeout(tick, READY_POLL_MS);
  };
  tick();
});

export const loadGoogleMaps = () => {
  if (mapsReady()) {
    return Promise.resolve(window.google.maps);
  }
  if (loadPromise) {
    return loadPromise;
  }

  // A tag is already on the page (public/index.html, or a previous caller):
  // never add a second copy of the API. Wait for that one instead.
  const existing = typeof document !== 'undefined' && document.querySelector(SCRIPT_SELECTOR);
  if (existing) {
    loadPromise = new Promise((resolve, reject) => {
      const done = () => waitForReady().then(resolve, reject);
      if (isGoogleMapsAvailable()) return done();
      existing.addEventListener('load', done, { once: true });
      existing.addEventListener('error', () => { loadPromise = null; reject(new Error('Failed to load Google Maps script')); }, { once: true });
      // The load event may already have fired before we attached; poll as a backstop.
      const started = Date.now();
      const tick = () => {
        if (isGoogleMapsAvailable()) return done();
        if (Date.now() - started > READY_TIMEOUT_MS) return reject(new Error('Google Maps did not become ready'));
        setTimeout(tick, READY_POLL_MS);
      };
      tick();
    });
    return loadPromise;
  }

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is not configured'));
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => waitForReady().then(resolve, reject);
    script.onerror = () => {
      // Allow a future retry by clearing the cached promise.
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};

export default loadGoogleMaps;
